import React, { useEffect, useRef, useState } from 'react';
import { useBreakpoint, useMatchMedia } from '@nike/nike-design-system-components';
import { search } from '../search';
import useLocale from './localeHook';
import { MAX_RESULTS_COUNT, MAX_RESULTS_COUNT_DESKTOP } from '../../constants';
import { AnalyticsContext } from '../../context/analyticsContext';

/**
 * Hook which will retrieve the list of search results based on the selected suggestion.
 * @param {object} selectedSuggestion
 * @returns {{visualSearchResults: object[], loading: boolean}}
 */

export default function useVisualSearch(selectedSuggestion) {
  const [visualSearchResults, setVisualSearchResults] = useState([]);
  const [useSquarishOnly, setUseSquarishOnly] = useState(false);
  const desktopMedia = useMatchMedia(useBreakpoint('gte', 'm'));
  const maxResults = desktopMedia ? MAX_RESULTS_COUNT_DESKTOP : MAX_RESULTS_COUNT;

  // keep track of the search suggestion in flight
  // using ref to preserve between re-renders and will not trigger re-render if changed
  const suggestionRef = useRef(selectedSuggestion);

  const [loading, setLoading] = useState(false);
  const { countryOrMarketplaceId: country, language } = useLocale();
  const searchOptions = {
    country,
    language,
    useCache: true,
  };
  const { analyticsClient } = React.useContext(AnalyticsContext);

  useEffect(() => {
    suggestionRef.current = selectedSuggestion;

    if (!selectedSuggestion?.searchText) {
      setVisualSearchResults([]);
      setUseSquarishOnly(false);
      setLoading(false);
      return;
    }

    setLoading(true);

    (async () => {
      const { objects = [] } = await search(selectedSuggestion.searchText, searchOptions);
      const results = objects.slice(0, maxResults);

      // Check if any product is missing portraitURL
      const anyMissingPortrait = results.some(product => !product?.colorwayImages?.portraitURL);
      setUseSquarishOnly(anyMissingPortrait);

      // Update the search results only if the search suggestion matches the current ref
      // fixes timing issue if a user hovers over multiple suggestions and the slowest response
      // will render even if it is not the currently selected suggestion
      if (suggestionRef.current === selectedSuggestion) {
        setVisualSearchResults(results);
        analyticsClient?.visualSearchCollectionViewed({
          searchTerm: selectedSuggestion.searchText,
          collection: results,
        });
      }

      setLoading(false);
    })();
  }, [selectedSuggestion, maxResults]);

  return { visualSearchResults, loading, useSquarishOnly };
}
